import queryKeys from 'config/queryKeys';
import KoalaButton from 'koala/components/Button';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Relationship, Workspace } from 'types';
import styled from 'styled-components';
import theme from 'theme';
import * as remoteApi from 'api/remote';
import * as workspaceUtils from 'utils/workspaceUtils';
import { Outcome } from 'types';
import KoalaLoader from 'koala/components/Loader';
import { shallowEqual, useSelector } from 'react-redux';
import UpgradeRequired from './UpgradeRequired';
import OutcomeMap from 'components/MapComponents/Outcome/OutcomeMap';
import { MapNode } from 'components/MapComponents';
import { ReactFlowProvider } from 'reactflow';

const Container = styled.div`
  width: 100%;
  height: 35rem;
  position: relative;
  overflow: hidden;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.N0};
  &.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 20;
  }
`;

const HeaderContainer = styled.div`
  padding: ${theme.spacing.x1} ${theme.spacing.x2};
  border-bottom: 1px solid ${theme.colors.N10};
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title {
    font-size: 1rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .actions {
    display: flex;
    gap: ${theme.spacing.x1};
    align-items: center;
  }
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background: rgba(255, 255, 255, 0.6);
`;

const UpgradeModal = styled.div`
  position: absolute;
  left: 50%;
  height: 18rem;
  z-index: 10;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  overflow: hidden;
  top: 50%;
  width: 30rem;
  margin-left: -15rem;
  margin-top: -9rem;
  background: #fff;

  @media ${theme.devices.mobile} {
    width: 100%;
    margin: 20rem 0 0 0;
    margin-top: -20rem;
    left: unset;
    overflow: auto;
    height: 30rem;
    max-height: 30rem;
  }
`;

interface Props {
  outcome: Outcome;
}

function OutcomeMinimap(props: Props) {
  const { outcome } = props;
  const { t } = useTranslation();
  const [fullScreenEnabled, setFullScreenEnabled] = useState(false);
  const [hasFetchedRelationships, setHasFetchedRelationships] = useState(false);
  const currentWorkspace: Workspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);

  const initialNode = useMemo(
    () => ({
      // eslint-disable-next-line
      ['synthetic-root']: {
        id: 'synthetic-root',
        children: [outcome.id],
        parents: [],
        type: 'default',
        name: 'Synthetic Root',
        meta: { expanded: true },
      },
      [outcome.id]: {
        id: outcome.id,
        children: [],
        parents: ['synthetic-root'],
        type: 'outcome',
        name: outcome.title,
        meta: { expanded: true, outcome, isFocus: true },
      },
    }),
    [outcome],
  );

  const [initialTree, setInitialTree] = useState<Record<string, MapNode>>(initialNode);

  useEffect(() => {
    setHasFetchedRelationships(false);
    setInitialTree(initialNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);

  // get relationships
  const queryKeyOutcomes = [queryKeys.relationships, outcome.id, 'outcomes'];
  const { isLoading: isLoadingOutcomes } = useQuery(queryKeyOutcomes, remoteApi.fetchRelationshipsForOutcome, {
    onSuccess: (response: any) => {
      const relationships: Relationship[] = response.data;

      const newTree = relationships.reduce(
        (acc, relationship) => {
          const outcomeId = outcome.id;
          const outcomeNode = acc[outcomeId];
          const syntheticRoot = acc['synthetic-root'];
          if (relationship.relationship_type !== 'contributes_to') {
            return acc;
          } else if (outcome.id === relationship.source_id) {
            // PARENTS OF OUTCOME
            const related = relationship.target;
            // add related id to parent array
            if (
              !outcomeNode.parents ||
              (outcomeNode.parents.length === 1 && outcomeNode.parents[0] === 'synthetic-root')
            ) {
              outcomeNode.parents = [];
              syntheticRoot.children = [];
            }
            if (!outcomeNode.parents.includes(related.id)) {
              outcomeNode.parents.push(related.id);
              syntheticRoot.children.push(related.id);
            }
            // create parent node
            const node = {
              id: related.id,
              meta: { expanded: true, outcome: related, hideExpandable: true },
              type: 'outcome',
              name: related.title,
              children: [outcomeId],
              parents: ['synthetic-root'],
            };
            return { ...acc, [outcomeId]: { ...outcomeNode }, [related.id]: node };
          } else if (outcome.id === relationship.target_id) {
            // CHILDREN OF OUTCOME
            const related = relationship.source;
            // add related id to children array if not already present
            if (!outcomeNode.children.includes(related.id)) {
              outcomeNode.children = [...outcomeNode.children, related.id];
            }

            // create child node
            const node = {
              id: related.id,
              meta: { expanded: false, outcome: related },
              type: 'outcome',
              name: related.title,
              children: [],
              parents: [outcomeId],
            };
            return { ...acc, [outcomeId]: { ...outcomeNode }, [related.id]: node };
          }

          return acc;
        },
        { ...initialTree },
      );
      setInitialTree(newTree);
      setHasFetchedRelationships(true);
    },
  });

  const fullScreenClass = fullScreenEnabled ? 'full-screen' : '';

  if (isLoadingOutcomes) {
    return <KoalaLoader />;
  }

  const hasPremiumSubscription = workspaceUtils.hasPremiumSubscription(currentWorkspace);

  // Check the upgrade required flag
  let upgradeRequired = !hasPremiumSubscription;
  return (
    <Container>
      {upgradeRequired && (
        <>
          <Mask />
          <UpgradeModal>
            <UpgradeRequired />
          </UpgradeModal>
        </>
      )}
      <Content className={fullScreenClass}>
        <HeaderContainer>
          <span className="title">Outcome Minimap</span>
          <div className="actions">
            <KoalaButton onClick={() => setFullScreenEnabled(!fullScreenEnabled)} appearance="secondary" size="small">
              {fullScreenEnabled ? t('shared.exitFullScreen') : t('shared.enterFullScreen')}
            </KoalaButton>
          </div>
        </HeaderContainer>
        {hasFetchedRelationships && (
          <ReactFlowProvider>
            <OutcomeMap initialTree={initialTree} rootId="synthetic-root" fullScreenEnabled={fullScreenEnabled} />
          </ReactFlowProvider>
        )}
      </Content>
    </Container>
  );
}

export default OutcomeMinimap;
